import React, { useState } from 'react'
import { Shield, Lock, Trash2, Eye, BarChart3 } from 'lucide-react'

interface RecipeConsentStepProps {
  onAccept: () => void
}

export default function RecipeConsentStep({ onAccept }: RecipeConsentStepProps) {
  const [accepted, setAccepted] = useState(false)

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Shield className="w-6 h-6 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Data Privacy Information</h1>
        </div>

        <div className="space-y-6 text-gray-600">
          {/* Why we collect this data */}
          <Section
            icon={<Eye className="w-5 h-5" />}
            title="Why we collect this data"
          >
            <p>
              Your answers allow us to create an accurate, safe, personalised cookbook for your nanny. This includes highlighting allergies, dietary needs, cultural requirements, nutritional goals, and household routines. Without this information, the cookbook cannot reliably meet your family's needs.
            </p>
          </Section>

          {/* What we collect */}
          <Section
            icon={<BarChart3 className="w-5 h-5" />}
            title="What we collect"
          >
            <p className="mb-3">We collect only the information you enter, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Meal preferences</li>
              <li>Allergies and dietary requirements</li>
              <li>Cultural or religious rules</li>
              <li>Cooking constraints</li>
              <li>Household routines</li>
              <li>Appliance availability</li>
            </ul>
            <p className="mt-3">We do not collect unnecessary personal identifiers unless you choose to provide them.</p>
          </Section>

          {/* How long we keep it */}
          <Section
            icon={<Lock className="w-5 h-5" />}
            title="How long we keep it"
          >
            <p>
              We retain cookbooks so that we can improve the product and improve the quality of recipes and recommendations.
            </p>
          </Section>

          {/* Who has access */}
          <Section
            icon={<Shield className="w-5 h-5" />}
            title="Who has access"
          >
            <p>
              Only the systems required to generate your family's cookbook can access this information. We do not share your family's data with advertisers or non-essential third parties.
            </p>
          </Section>

          {/* How we protect it */}
          <Section
            icon={<Lock className="w-5 h-5" />}
            title="How we protect it"
          >
            <ul className="list-disc pl-5 space-y-1">
              <li>Encryption in transit and at rest</li>
              <li>Strictly limited and logged access</li>
              <li>Regular data minimisation reviews</li>
              <li>Redaction of identifying details from free-text fields before analytics use</li>
            </ul>
          </Section>

          {/* Research and product improvement */}
          <Section
            icon={<BarChart3 className="w-5 h-5" />}
            title="Research and product improvement"
          >
            <p>
              We may use anonymised, aggregated data to improve recipes and understand common user needs. This aggregated data cannot be traced back to any individual family.
            </p>
          </Section>

          {/* Deleting your data */}
          <Section
            icon={<Trash2 className="w-5 h-5" />}
            title="Deleting your data"
          >
            <p>
              If you would like your data deleted, please email{' '}
              <a href="mailto:rachel.carrell@korukids.co.uk" className="text-emerald-600 hover:underline">
                rachel.carrell@korukids.co.uk
              </a>.
            </p>
          </Section>
        </div>

        {/* Consent checkbox */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-gray-700">
              I understand how my data will be used and consent to Koru Kids processing it to create my family's cookbook.
            </span>
          </label>
        </div>

        {/* Continue button */}
        <div className="mt-6">
          <button
            onClick={onAccept}
            disabled={!accepted}
            className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
              accepted
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue to Questionnaire
          </button>
        </div>
      </div>
    </div>
  )
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-emerald-600">{icon}</span>
        <h2 className="font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="pl-7 text-sm">{children}</div>
    </div>
  )
}
